import React from 'react';

import MachinistLayout from '@/components/Layout/MachinistLayout';
import LayoutRoute from '@/components/Layout/LayoutRoute';
import EmptyLayoutLogoutFooter from '@/components/Layout/EmptyLayoutWithLogoutFooter';

import { BrowserRouter, Redirect, Switch, useLocation } from 'react-router-dom';
import '@/styles/reduction.scss';
import { useAuthState } from './utils/auth';

import {
  AllAuthorizedContextProvider,
  ManageFilterOptions,
} from '@/hooks/useFilter';
import QRHandlerPage from '@/modules/app/QRHandlerPage';
import { ManageProjectOptions } from '@/hooks/useProjectOptions';
import { RegisterSentryUser } from '@/utils/RegisterSentryUser';
import { RegisterPostHogUser } from '@/utils/RegisterPostHogUser';
import MaybeMaintenanceMode from '@/components/MaybeMaintenanceMode';
import { cn } from '@/utils/utils';
import { GuidedTourProvider } from '@/components/GuidedTourProvider';
import { SentryTracedRoute } from '@/modules/app/SentryRoute';
import { ConfirmModalToggleProvider } from '@/components/Modals/ConfirmUnsavedDataModal';
import { BilderitLogoHorizontal } from '@/components/BilderitLogoHorizontal';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { BreadcrumbsProvider } from '@/components/ConnectedBreadcrumbs';

// pages
import AuthPage from '@/modules/app/AuthPage';
import SettingsPage from '@/modules/app/SettingsPage';
import ProfilePage from '@/modules/app/ProfilePage';
import CreateCompanyPage from '@/modules/app/CreateCompanyPage';
import EmployeeConfirmPage from '@/modules/app/EmployeeConfirmPage';
import { IdentityClaimMergeResumer } from '@/modules/identity_claim/IdentityClaimMergeResumer';
import { IdentityClaimEntry } from '@/modules/identity_claim/IdentityClaimEntry';
import { EmailChangeDialog } from '@/modules/email_change/EmailChangeDialog';
import IdentityClaimPage from '@/modules/identity_claim/IdentityClaimPage';
import { IDENTITY_CLAIM_ROUTE } from '@/modules/identity_claim/identityClaimConstants';

// lazy load internal pages
const MachinistRouter = React.lazy(
  () => import('@/modules/maquinista/MachinistRouter')
);

const getBasename = () => {
  return `/${import.meta.env.BASE_URL.split('/').pop()}`;
};

const App: React.FC = () => (
  <BrowserRouter basename={getBasename()}>
    <ManageFilterOptions />
    <ManageProjectOptions />
    <GuidedTourProvider>
      <BreadcrumbsProvider>
        <div
          className={cn('fixed h-full w-full grid grid-rows-[auto_1fr_auto]')}
        >
          <React.Suspense
            fallback={
              <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-sidebar p-6 md:p-10">
                <div className="flex w-full max-w-sm flex-col gap-6">
                  <a
                    href="/"
                    className="flex items-center gap-2 self-center font-medium"
                  >
                    <BilderitLogoHorizontal className="h-12" />
                  </a>
                  <LoadingMessageCard message="Cargando..." />
                </div>
              </div>
            }
          >
            <ConfirmModalToggleProvider>
              <AuthPage>
                <RegisterSentryUser />
                <RegisterPostHogUser />
                <IdentityClaimMergeResumer />
                <IdentityClaimEntry />
                <EmailChangeDialog />
                <MaybeMaintenanceMode>
                  <HasCompany>
                    <AllAuthorizedContextProvider>
                      <Switch>
                        {/* // once we're past AuthPage, we dont need the /login route,
              remove it so it doesn't get taken for a company */}
                        <SentryTracedRoute
                          path="/login"
                          exact
                          strict
                          trackPageview={false}
                          render={() => <Redirect to="/" />}
                        />
                        <SentryTracedRoute
                          path="/:url*(/+)"
                          exact
                          strict
                          trackPageview={false}
                          render={({ location }) => (
                            <Redirect
                              to={location.pathname.replace(/\/+$/, '')}
                            />
                          )}
                        />
                        {/* Removes duplicate slashes in the middle of the URL */}
                        <SentryTracedRoute
                          path="/:url(.*//+.*)"
                          exact
                          strict
                          trackPageview={false}
                          render={({ match }) => (
                            <Redirect
                              to={`/${match.params.url.replace(/\/\/+/, '/')}`}
                            />
                          )}
                        />
                        <LayoutRoute
                          exact
                          path={`/create`}
                          title="Crea una compañia"
                          layout={EmptyLayoutLogoutFooter}
                          component={CreateCompanyPage}
                          noSidebar={true}
                        />
                        <LayoutRoute
                          exact
                          path={`/(confirm-employee|notifications)`}
                          title="Notificaciones"
                          layout={MachinistLayout}
                          component={EmployeeConfirmPage}
                        />
                        <LayoutRoute
                          exact
                          path={`/settings`}
                          title="Settings"
                          layout={MachinistLayout}
                          component={SettingsPage}
                        />
                        <LayoutRoute
                          exact
                          path={`/profile`}
                          title="Perfil"
                          layout={MachinistLayout}
                          component={ProfilePage}
                        />
                        <LayoutRoute
                          exact
                          path={IDENTITY_CLAIM_ROUTE}
                          title="Hacé tuya tu cuenta"
                          layout={MachinistLayout}
                          component={IdentityClaimPage}
                        />
                        <SentryTracedRoute
                          exact
                          path={`/QR/:version/`}
                          component={QRHandlerPage}
                        />
                        <LayoutRoute
                          exact
                          path={''}
                          title="Maquinista"
                          layout={MachinistLayout}
                          component={MachinistRouter}
                        />
                      </Switch>
                    </AllAuthorizedContextProvider>
                  </HasCompany>
                </MaybeMaintenanceMode>
              </AuthPage>
            </ConfirmModalToggleProvider>
          </React.Suspense>
        </div>
      </BreadcrumbsProvider>
    </GuidedTourProvider>
  </BrowserRouter>
);

const HasCompany: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { hasCompany } = useAuthState();

  if (!hasCompany && location.pathname !== '/create') {
    // If the user doesn't have a company yet, make him choose or create one
    return <Redirect to={'/create'} />;
  } else {
    return <>{children}</>;
  }
};

export default App;
